import { AreaPageListing_ListingType, Provider, StandardStatus, type BoundaryListing } from '@zoocasa/go-search';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { AgreementTypes } from 'types/agreement-storage-types';
import { cleanDashedString } from 'data/addresses';
import { isVOWProvider } from 'utils/compliance';

import type { ListingCardData } from 'components/listing-card/listing-card';

/**
 * Adapts a go-search BoundaryListing (from /api/map/pins representative) to the
 * ListingCardData interface consumed by the side panel listing cards.
 *
 * Mirrors AreaPageListingCardData but backed by the lighter BoundaryListing proto.
 */
export class BoundaryListingCardData implements ListingCardData {
  constructor(private listing: BoundaryListing) {}

  get id() {
    return this.listing.id;
  }

  get imageUrl() {
    const key = this.listing.imageRootStorageKey;
    if (!key || key.length === 0) return '';
    if (key.startsWith('http')) return key;
    return `https://cdn0.zoocasa.com/${key}.jpg`;
  }

  get isImageReady() {
    return (this.listing.imageRootStorageKey?.length ?? 0) > 0;
  }

  get imageDesc() {
    return this.listing.address;
  }

  get slug() {
    return this.listing.slug;
  }

  get addressPathWithFallback() {
    const raw = this.listing.addressUrlAbsolutePath || this.listing.listingUrlAbsolutePath;
    if (raw && !raw.startsWith('/real-estate/')) {
      return cleanDashedString(raw);
    }
    const slug = this.listing.slug;
    const city = this.listing.subDivision?.toLowerCase();
    const prov = this.listing.province?.toLowerCase();
    if (slug && city && prov) {
      const citySlug = cleanDashedString(`${city}-${prov}`);
      const addressSlug = slug.replace(`-${citySlug}`, '');
      return cleanDashedString(`/${citySlug}-real-estate/${addressSlug}`);
    }
    return cleanDashedString(raw);
  }

  get isCrea() {
    return this.listing.providerId === Provider.PROVIDER_CREA_DDF;
  }

  get isItsoVow() {
    return this.listing.providerId === Provider.PROVIDER_ITSO_VOW;
  }

  get isPillar9Vow() {
    return this.listing.providerId === Provider.PROVIDER_PILLAR9_VOW;
  }

  get isRebgvVow() {
    return this.listing.providerId === Provider.PROVIDER_REBGV_VOW;
  }

  get city() {
    return this.listing.subDivision || null;
  }

  get province() {
    return this.listing.province || null;
  }

  get isPrivate() {
    return isVOWProvider(this.listing.providerId);
  }

  get isSold() {
    return this.listing.standardStatus === StandardStatus.STANDARD_STATUS_CLOSED;
  }

  get position() {
    if (!this.listing.position) return null;
    const [lat, lng] = this.listing.position.split(',').map(Number);
    if (isNaN(lat) || isNaN(lng)) return null;
    return { latitude: lat, longitude: lng };
  }

  getStreet = (isAuthenticated: boolean) => {
    if (!isAuthenticated && this.isPrivate) {
      return capitalizeWords(this.listing.address?.split(',')[0] ?? '');
    }
    const unit = this.listing.unit;
    const parts = this.listing.address?.split(',').map(s => s.trim()) ?? [];
    // BoundaryListing.address may include the unit as the first comma segment
    // (e.g. "507, 123 Main St, Etobicoke, ON"). Skip it to avoid "507 - 507".
    const streetIdx = unit && parts[0] === unit ? 1 : 0;
    const streetPart = parts[streetIdx] ?? parts[0] ?? '';
    return capitalizeWords(unit?.length > 0 ? `${unit} - ${streetPart}` : streetPart);
  };

  get providerId() {
    return this.listing.providerId;
  }

  get isSignInAndTermsOfUseRequired() {
    return this.listing.termsOfUseRequired ?? false;
  }

  get mlsNum() {
    return this.listing.mls;
  }

  get brokerage() {
    return this.listing.brokerage;
  }

  get isRental() {
    return this.listing.listingType === AreaPageListing_ListingType.rent;
  }

  get displayAddress() {
    return this.listing.displayAddress?.internetAddressDisplay ?? true;
  }

  get isTreb() {
    return !this.isCrea;
  }

  get agreementType() {
    if (this.isPillar9Vow) return AgreementTypes.PILLAR9_VOW;
    if (this.isRebgvVow) return AgreementTypes.REBGV_VOW;
    if (this.isItsoVow) return AgreementTypes.ITSO_VOW;
    if (this.isCrea) return AgreementTypes.CREA;
    return undefined;
  }

  get price() {
    return this.listing.price;
  }

  get soldPrice() {
    return this.listing.SoldPrice > 0 ? this.listing.SoldPrice : null;
  }

  get isSoldWithoutSoldPrice() {
    return this.isSold && !this.soldPrice;
  }

  get statusLabel() {
    const lastStatus = this.listing.lastStatus;
    if (lastStatus) {
      const map: Record<string, string> = {
        Bom: 'Back on Market', Dft: 'Deal Fell Through', Exp: 'Expired',
        Ext: 'Extended', Lc: 'Leased Conditionally', Lsd: 'Leased',
        New: 'New', Pc: 'Price Change', Sc: 'Sold Conditionally',
        Sld: 'Sold', Sus: 'Suspended', Ter: 'Terminated', Pen: 'Pending',
      };
      const label = map[lastStatus];
      if (label && label !== 'New') return label;
    }

    switch (this.listing.standardStatus) {
      case StandardStatus.STANDARD_STATUS_ACTIVE:
      case StandardStatus.STANDARD_STATUS_COMING_SOON:
        return this.isRental ? 'For Rent' : 'For Sale';
      case StandardStatus.STANDARD_STATUS_CLOSED:
        return this.isRental ? 'Leased' : 'Sold';
      case StandardStatus.STANDARD_STATUS_PENDING:
      case StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT:
        return 'Pending';
      case StandardStatus.STANDARD_STATUS_EXPIRED:
        return 'Expired';
      case StandardStatus.STANDARD_STATUS_CANCELED:
      case StandardStatus.STANDARD_STATUS_WITHDRAWN:
      case StandardStatus.STANDARD_STATUS_DELETE:
        return 'Inactive';
      default:
        return 'Inactive';
    }
  }

  get soldAt() { return null; }
  get expiredAt() { return null; }
  get addedAt() { return this.listing.createdAt || null; }
  get updatedOn() { return this.listing.updatedOn || null; }
  get standardStatus() { return this.listing.standardStatus; }
  get onMarketDate() { return this.listing.onMarketDate ?? null; }

  get bedrooms() { return this.listing.bedrooms; }
  get bathrooms() { return this.listing.bathrooms; }

  get avgSqft() {
    if (!this.listing.squareFootage) return null;
    const { gt, gte, lt, lte } = this.listing.squareFootage;
    const min = lt || lte;
    const max = gt || gte;
    if (min && max) return Math.ceil((min + max) / 2);
    return min || max || null;
  }

  get sqftLabel() {
    if (!this.listing.squareFootage) return '';
    const { gt, gte, lt, lte } = this.listing.squareFootage;
    const min = gt || gte;
    const max = lt || lte;
    const sqft = max === 499 ? '0-499' : [min, max].filter(Boolean).join('–');
    return `${!sqft?.trim()?.length ? 'N/A' : sqft.trim()} sqft`;
  }
}
